import { useCallback, useEffect, useRef, useState } from 'react'

import { useEncodingDevice, useSnackbar } from 'contexts'
import { ENCODABLE_FAMILIES } from 'components'
import type { TagFamily } from 'components'
import { api } from 'services'
import { getErrorMessage } from 'utils'

/**
 * VERA-790 — the in-house encode SESSION machinery, extracted so the single-package "Add tags"
 * modal (`EncodeTagsModal`) and the bulk assembly-line modal (`BulkEncodeTagsModal`) share ONE
 * copy of the reader loop, provision queue, running list, and cancel/reassign logic. The hard-won
 * stability of this loop (stale-arm supersede, de-bounce, stuck-tag surfacing, keepAlive lock)
 * only has to be maintained in a single place.
 *
 * The hook is session-mechanical only — it owns the tap→encode→provision pipeline for ONE target
 * at a time. The CONTAINER owns target resolution (which package/company), the lock/family/product
 * config, target-capacity display, and the auto-start / "Next box" orchestration; it hands the
 * current config in via `config` and drives the session with `runSession` / `stopSession` /
 * `resetSession`.
 */

/** Why a tag was quarantined — drives the remediation hint (retry vs discard). */
export type QuarantineKind = 'read' | 'encode' | 'provision' | 'conflict' | 'full'

export const QUARANTINE_HINT: Record<QuarantineKind, string> = {
  // Couldn't read the chip UID — usually a bad tap; re-tap.
  read: "Couldn't read the tag — re-tap it.",
  // The SUN write didn't land on the chip — RF/bad tag; re-tap, discard if it repeats.
  encode: "Didn't write to the chip — re-tap; discard the tag if this repeats.",
  // Encoded on the chip but the server didn't save it — re-tap to re-provision.
  provision: 'Encoded on the chip but not saved — re-tap to retry.',
  // VERA-790 Part C — this uid already belongs to a DIFFERENT package; the row's actual reason
  // string (set from the server's `existingPackageName`) always overrides this fallback.
  conflict: 'Already provisioned into another package.',
  // VERA-790 — the package is at capacity; this tag was encoded but NOT added. Re-tapping won't
  // help — the server's `X/Y` message overrides this fallback.
  full: 'This package is full.',
}

export type EncodedTag = {
  uid: string
  locked: boolean
  // 'provisioning' = the chip write succeeded and the tag is queued for its (async, non-blocking)
  // server-side provision call — the reader is already re-armed for the next tap by this point.
  // 'already' = a same-package re-tap (VERA-790) — the tag was ALREADY in this package before this
  // tap, so it's neither a fresh success nor an error; rendered as a distinct neutral/info row.
  status: 'provisioned' | 'quarantined' | 'provisioning' | 'already'
  kind?: QuarantineKind
  reason?: string
  // VERA-790 Part C — set only on a 'conflict' quarantine row: the original provision job,
  // replayed with `reassign: true` when the operator clicks "Add here instead".
  conflictJob?: ProvisionJob
}

/** A queued provision-POST job (VERA-790 Change C — decoupled from the reader tap for cadence). */
export type ProvisionJob = {
  chipUid: string
  locked: boolean
  family: TagFamily
  lock: boolean
  productSku?: string
  productTitle?: string
  productImage?: string
  sdmMetaReadKey?: string
  sdmFileReadKey?: string
  target: EncodeSessionTargetRef
  // VERA-790 Part C — confirms moving a tag that's already in a DIFFERENT package. Only ever set
  // true on the operator-triggered retry from a 'conflict' quarantine row's "Add here instead".
  reassign?: boolean
}

export type EncodeSessionTargetRef =
  | { kind: 'package'; id: string }
  | { kind: 'company'; id: string }
  // VERA-790 — encode a tag with no company + no package: a Vera-owned, unassigned tag, assignable
  // to a package later. The provision goes to the company-direct endpoint with no companyId.
  | { kind: 'unassigned' }

/**
 * The full config for ONE session run, passed into `runSession` at call time (NOT via a ref) so a
 * bulk box-switch can't race a stale config: the values travel with the target and are fixed for
 * that run's whole loop. `lock` is UI-toggleable but disabled once the session is active, so it's
 * effectively fixed at run start too.
 */
export type EncodeSessionRun = {
  target: EncodeSessionTargetRef
  // Null when the variant has no tag_family — runSession refuses to arm (no guessed family).
  family: TagFamily | null
  lock: boolean
  productSku?: string
  productTitle?: string
  productImage?: string
}

// Bounded concurrency for the provision queue — caps parallel secret-store writes without
// bottlenecking the operator's tap cadence on a single in-flight request.
const PROVISION_CONCURRENCY = 4
// VERA-790 Phase 2 — how often an active session pings the reader-helper to hold its
// single-session lock. Comfortably under the helper's 15s idle-release window.
const KEEP_ALIVE_INTERVAL_MS = 10000

export type UseEncodeSessionReturn = ReturnType<typeof useEncodeSession>

export const useEncodeSession = (opts?: {
  /** VERA-790 tap-box-to-assign — called with a package HANDLE url when the operator taps a box's
   *  label mid-session. Kept in a ref so the loop always calls the latest closure. */
  onPackageHandle?: (url: string) => void
}) => {
  const { encode, subscribeScan, keepAlive } = useEncodingDevice()
  const { open: notification } = useSnackbar()

  const onPackageHandleRef = useRef(opts?.onPackageHandle)
  useEffect(() => {
    onPackageHandleRef.current = opts?.onPackageHandle
  }, [opts?.onPackageHandle])

  const [encoded, setEncoded] = useState<EncodedTag[]>([])
  const [sessionActive, setSessionActive] = useState(false)
  const [waiting, setWaiting] = useState(false)
  // VERA-790 Part B — the package's live true total (existing + this session's provisions,
  // re-encodes/moves counted once — server-sourced, never computed client-side). Null for
  // company-direct sessions (no package to total).
  const [packageTagCount, setPackageTagCount] = useState<number | null>(null)
  // Inline alert (info/warning/error), replaces bare red text. Timeouts are info, not errors.
  const [alert, setAlert] = useState<{ severity: 'info' | 'warning' | 'error'; message: string } | null>(null)
  // Filter the running list to just the failures — so at 500 tags the operator can find and
  // re-tap the quarantined ones instead of scrolling for scattered red rows.
  const [showQuarantinedOnly, setShowQuarantinedOnly] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  const runningRef = useRef(false)
  // Run generation — bumped by every runSession start and by stopSession. The loop captures its
  // generation and bails the instant a newer run supersedes it, so restarting the session for a
  // new target (e.g. the operator switches company) can't leave the previous loop running as a
  // zombie that ping-pongs `encode()` arms with the new one. (runningRef alone can't do this: a
  // new run sets it back to true before the old loop notices.)
  const runGenRef = useRef(0)
  // VERA-790 Phase 2 — while a session is active, hold the reader-helper's single-session lock
  // with a periodic `keepAlive` (well under the helper's 15s idle-release), since the session
  // sits ARMED-AND-WAITING for a tap most of the time — without this it looks idle to the helper
  // after 15s and a second tab could steal the reader mid-wait. Cleared on stop/unmount below.
  const keepAliveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Upsert a row by chip UID: re-tapping a tag REPLACES its prior row (so a re-tap of a
  // quarantined tag clears the red row and moves it to provisioned — no stale duplicates).
  // Rows with no real UID can't be deduped, so they get a unique key.
  const noUidSeq = useRef(0)
  const upsertEncoded = useCallback((row: EncodedTag) => {
    const key = row.uid && row.uid !== 'unknown' ? row.uid : `no-uid-${noUidSeq.current++}`
    const r = { ...row, uid: key }
    setEncoded((prev) => [r, ...prev.filter((t) => t.uid !== key)])
  }, [])

  // Bounded-concurrency provision queue (VERA-790 Change C): the reader loop enqueues a job and
  // re-arms immediately rather than awaiting the provision POST, so operator cadence is bounded
  // by the reader, not the secret-store write. Refs (not state) since the pump loop reads/mutates
  // them synchronously between async POSTs.
  const provisionQueueRef = useRef<ProvisionJob[]>([])
  const activeProvisionCountRef = useRef(0)
  const pumpProvision = useCallback(() => {
    while (
      activeProvisionCountRef.current < PROVISION_CONCURRENCY &&
      provisionQueueRef.current.length > 0
    ) {
      const job = provisionQueueRef.current.shift()!
      activeProvisionCountRef.current += 1
      const url =
        job.target.kind === 'package'
          ? `/packages/${job.target.id}/tags/encode-provision`
          : '/packages/tags/encode-provision'
      const body: Record<string, any> = {
        chipUid: job.chipUid,
        family: job.family,
        productSku: job.productSku,
        productTitle: job.productTitle,
        productImage: job.productImage,
        lock: job.lock,
        sdmMetaReadKey: job.sdmMetaReadKey,
        sdmFileReadKey: job.sdmFileReadKey,
        reassign: job.reassign === true,
      }
      if (job.target.kind === 'company') body.companyId = job.target.id
      api
        .post(url, body)
        .then(({ data }) => {
          // VERA-790 Part C — this uid is already provisioned into a DIFFERENT package. Quarantine
          // it with the offending package's name and an "Add here instead" retry action, rather
          // than the generic provision-failure row (checked first: a conflict response also has
          // `success: false`, so it must be distinguished before the generic failure branch).
          if (data?.conflict) {
            upsertEncoded({
              uid: job.chipUid,
              locked: job.locked,
              status: 'quarantined',
              kind: 'conflict',
              reason: data?.existingPackageName
                ? `Already in package "${data.existingPackageName}"`
                : QUARANTINE_HINT.conflict,
              conflictJob: job,
            })
            return
          }
          // VERA-790 — the package is full (capacity guard). The tag was encoded but NOT added;
          // re-tapping won't help, so it's an amber "full" row (not the generic retry failure).
          if (data?.packageFull) {
            upsertEncoded({
              uid: job.chipUid,
              locked: job.locked,
              status: 'quarantined',
              kind: 'full',
              reason: data?.error || QUARANTINE_HINT.full,
            })
            if (typeof data?.packageTagCount === 'number') {
              setPackageTagCount(data.packageTagCount)
            }
            return
          }
          if (data?.success) {
            // VERA-790 — a same-package re-tap: this uid was already in THIS package before this
            // tap (not a fresh add). Mark it distinctly (neutral/info), not the green success row.
            upsertEncoded(
              data?.alreadyInPackage
                ? {
                    uid: job.chipUid,
                    locked: job.locked,
                    status: 'already',
                    reason: 'Already in this package.',
                  }
                : { uid: job.chipUid, locked: job.locked, status: 'provisioned' },
            )
            // VERA-790 Part B — the DB-true package total after this save (undefined for the
            // company-direct/package-less test path, which has no package total to show). Already
            // reflects the true total either way, so a re-tap correctly doesn't bump the count.
            if (typeof data?.packageTagCount === 'number') {
              setPackageTagCount(data.packageTagCount)
            }
            return
          }
          // VRG-806 — SHOW THE SERVER'S REASON. The `packageFull` branch 20 lines up already
          // does `data?.error || HINT`; this one discarded `data.error` and always printed the
          // canned "re-tap to retry". That is not merely less informative, it is WRONG ADVICE
          // for a whole class of failure: on prod, 2026-11-02, every encode returned
          // "GOOGLE_SECRET_MANAGER_PROJECT_ID is not configured" (the var was missing from
          // terraform/prod — VRG-806 adds it), and no number of re-taps could ever have fixed
          // it. Each retry physically wrote another chip and spent it. The operator had no way
          // to see why, because the use-case's own `{success: false, error}` was dropped here.
          upsertEncoded({
            uid: job.chipUid,
            locked: job.locked,
            status: 'quarantined',
            kind: 'provision',
            reason: data?.error || QUARANTINE_HINT.provision,
          })
        })
        .catch(() => {
          // Network/transport failure — there is no server body to read, so the canned hint
          // stands here and re-tapping genuinely is the right advice (unlike the branch above,
          // where the server answered and told us why).
          upsertEncoded({
            uid: job.chipUid,
            locked: job.locked,
            status: 'quarantined',
            kind: 'provision',
            reason: QUARANTINE_HINT.provision,
          })
        })
        .finally(() => {
          activeProvisionCountRef.current -= 1
          pumpProvision()
        })
    }
  }, [upsertEncoded])

  const provisioned = encoded.filter((t) => t.status === 'provisioned').length
  const quarantined = encoded.filter((t) => t.status === 'quarantined').length
  const provisioning = encoded.filter((t) => t.status === 'provisioning').length
  const alreadyInPackageCount = encoded.filter((t) => t.status === 'already').length
  const visibleRows = showQuarantinedOnly
    ? encoded.filter((t) => t.status === 'quarantined')
    : encoded

  // VERA-790 Part B — seed `packageTagCount` with a package's REAL current total. A fresh fetch
  // (not a client-side sum) so a box that already has existing tags from a prior session — or from
  // re-encodes/moves that don't net-add a tag — starts the counter correct. Container calls this
  // when its package target changes. No-op / caller-skipped for company-direct sessions.
  const seedPackageCount = useCallback((packageId: string) => {
    setPackageTagCount(null)
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await api.get(`/packages/${packageId}/tags`)
        if (!cancelled) setPackageTagCount(Array.isArray(data?.data) ? data.data.length : 0)
      } catch {
        // Leave it null — the display falls back to the session tally until a provision response
        // fills it in (see pumpProvision's success handler).
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Continuous session: arm the reader, hand the tapped tag's provision off to the async queue,
  // then re-arm immediately — until stopSession, or an encode error (e.g. no tag presented in
  // time) pauses it. Each tag is one physical tap. The full run config (target + family + lock +
  // product identity) is passed in and fixed for this run, so a bulk box-switch can start the next
  // box the INSTANT it resolves without racing a stale config.
  const runSession = useCallback(
    async (run: EncodeSessionRun) => {
      const { target, family, lock, productSku, productTitle, productImage } = run
      // VERA-790 — hard tag-type enforcement (no inference, ever). Refuse to arm the reader when the
      // variant has no tag_family, or has one the reader can't build yet — a wrong family breaks
      // encode + verification, so a blocked encode beats a guessed one. Copy stays generic (no chip
      // family named) so the tag type isn't exposed in the UI.
      if (!family) {
        setAlert({ severity: 'warning', message: 'Missing tag type. Set it on this variant in Shopify, then try again.' })
        return
      }
      if (!ENCODABLE_FAMILIES.includes(family)) {
        // "yet" was correct, and this is now the narrower set it was always meant to describe.
        // Connect (NTAG 213) is no longer in it — a Connect tag CAN be encoded, written and
        // locked, it simply cannot carry the rotating SDM proof a 424 does, which is why VRG-252
        // gave it its own method. What remains here is ST25, whose reader-side write is pending
        // hardware. The set is DERIVED from each family's encode strategy, so a family that gains
        // a reader path stops matching this branch by declaring it, not by editing a list.
        setAlert({
          severity: 'warning',
          message: 'This tag type can’t be encoded on the desk reader yet.',
        })
        return
      }
      // Claim a fresh generation; any previously-running loop is now stale and will bail.
      const myGen = ++runGenRef.current
      runningRef.current = true
      setSessionActive(true)
      setAlert(null)
      // VERA-790 Phase 2 — hold the reader lock for the whole session: the loop below spends most
      // of its time ARMED-AND-WAITING inside `encode()` (up to ~5min per tap), which otherwise
      // looks idle to the reader-helper after 15s and lets a second tab steal ownership mid-wait.
      if (keepAliveIntervalRef.current) clearInterval(keepAliveIntervalRef.current)
      keepAliveIntervalRef.current = setInterval(() => keepAlive(), KEEP_ALIVE_INTERVAL_MS)
      // Guard against a stuck tag hot-spinning the loop: a tag left on the reader (or an
      // incompatible/failing one) makes every arm immediately "card present → write fails". Back
      // off between failures, and if the SAME stuck condition persists more than a few seconds,
      // SURFACE it so the operator removes it. The session stays alive; the indicator clears the
      // moment a tag encodes or the reader goes idle (tag removed).
      let failStreakStart = 0
      const FAIL_BACKOFF_MS = 1200
      const STUCK_TAG_MS = 3000
      while (runningRef.current && runGenRef.current === myGen) {
        setWaiting(true)
        const result = await encode({ lock, family })
        setWaiting(false)
        // Bail if stopped OR superseded by a newer run (target switched) — the newer loop owns
        // the reader now, so this one must not consume its arm.
        if (!runningRef.current || runGenRef.current !== myGen) break

        if (result.ok && result.uid) {
          // Success — clear any stuck-tag indicator + streak, then fall through to provisioning.
          failStreakStart = 0
          setAlert(null)
        } else {
          const msg = result.error || ''
          // VERA-790 tap-box-to-assign — the non-424 tag tapped is a package HANDLE label (the
          // reader read its `/pkg/<id>` URL). Hand it to the container, which assigns the tags
          // encoded this session to that box. Keep the session armed; this is NOT an error, so no
          // alert. Checked FIRST — a handle result also carries wrongTagType, so it must win over
          // the generic wrong-tag branch below.
          if (!result.ok && result.packageHandleUrl) {
            failStreakStart = 0
            setAlert(null)
            onPackageHandleRef.current?.(result.packageHandleUrl)
            await new Promise((r) => setTimeout(r, FAIL_BACKOFF_MS))
            continue
          }
          // No tag in the arm window, or the tag was removed → idle, not a failure. Clear + re-arm.
          if (!result.ok && /no tag|in time|present/i.test(msg)) {
            failStreakStart = 0
            setAlert(null)
            continue
          }
          // Reader/helper gone → fatal. Pause the session with an alert.
          if (!result.ok && /not connected|unavailable|failed to send/i.test(msg)) {
            setAlert({ severity: 'error', message: 'Reader disconnected — reconnect and resume.' })
            runningRef.current = false
            setSessionActive(false)
            break
          }
          // VERA-790 Phase 2 — a DIFFERENT session currently owns the reader (the helper's single-
          // session lock rejected this arm outright). NOT a stuck/bad tag, so it's checked — with
          // its own message — before the generic stuck-tag fallback below. Keep looping: this
          // session auto-claims the lock the instant the other one disconnects or goes idle (15s).
          if (!result.ok && (result.busy || /reader_busy/i.test(msg))) {
            failStreakStart = 0
            setAlert({
              severity: 'warning',
              message:
                'The reader is in use by another session. Finish or close that session, and this one will pick it up automatically.',
            })
            await new Promise((r) => setTimeout(r, FAIL_BACKOFF_MS))
            continue
          }
          // VRG-252 — the chip is already permanently locked, so it can never accept a write. The
          // generic stuck-tag backoff below would tell the operator to "remove it and present the
          // next one" only after 3s of silent retries, and a re-tap of the SAME tag would loop; a
          // locked tag is a terminal, per-tag fact and says so immediately. Quarantined as a read
          // failure would be equally misleading — nothing failed, the tag is simply spent.
          if (!result.ok && result.alreadyLocked) {
            failStreakStart = 0
            upsertEncoded({
              uid: result.uid || 'unknown',
              locked: true,
              status: 'quarantined',
              kind: 'encode',
              reason: 'Already locked — this tag can’t be re-encoded. Set it aside.',
            })
            await new Promise((r) => setTimeout(r, FAIL_BACKOFF_MS))
            continue
          }
          // VRG-252 — the URL was written correctly and only the LOCK failed. The tag is usable
          // but unlocked, which is a materially different state from "the write failed": re-tapping
          // will not help (it is already encoded) and shipping it from a locked run is wrong. Say
          // exactly that rather than folding it into the generic retry copy.
          if (!result.ok && result.wroteButDidNotLock) {
            failStreakStart = 0
            upsertEncoded({
              uid: result.uid || 'unknown',
              locked: false,
              status: 'quarantined',
              kind: 'encode',
              reason: result.error || 'Encoded, but could not be locked. Set it aside.',
            })
            await new Promise((r) => setTimeout(r, FAIL_BACKOFF_MS))
            continue
          }
          // Wrong tag type — the presented chip class does not match the family this session is
          // encoding. The reader detects it up front and never attempts the write, so this is its
          // own case (not the generic stuck-tag backoff below). Keep the session alive — re-arm
          // after a short backoff so the operator can present the right tag.
          //
          // VRG-252: the copy no longer names the 424. The check is symmetric now (a 424 presented
          // during a Connect session is refused too, because writing a proofless static URL onto a
          // chip that can carry a proof downgrades it — permanently, if the session locks), so
          // "present a Vera 424 tag" would be actively wrong half the time. It also never named
          // the chip family to the operator by design; it just did so accidentally.
          if (!result.ok && (result.wrongTagType || /wrong_tag_type/i.test(msg))) {
            failStreakStart = 0
            setAlert({
              severity: 'warning',
              message:
                "That isn't the right tag type for this package. Remove it and present one of the tags this package was ordered with.",
            })
            await new Promise((r) => setTimeout(r, FAIL_BACKOFF_MS))
            continue
          }
          // A write/read failure — most likely the same tag still sitting on the reader (or a bad
          // one). Back off; once it's persisted a few seconds, surface it instead of flooding rows.
          if (!failStreakStart) failStreakStart = Date.now()
          if (Date.now() - failStreakStart >= STUCK_TAG_MS) {
            setAlert({
              severity: 'warning',
              message:
                'Same tag still on the reader, or it can’t be written. Remove it and present the next one.',
            })
          }
          await new Promise((r) => setTimeout(r, FAIL_BACKOFF_MS))
          continue
        }

        // Chip write succeeded — mark it in-flight and re-arm the reader NOW; the provision POST
        // (secret-store write, ~seconds) runs off a bounded queue so it never bottlenecks the tap
        // cadence. The row flips to provisioned/quarantined asynchronously when the job resolves.
        upsertEncoded({ uid: result.uid, locked: Boolean(result.locked), status: 'provisioning' })
        provisionQueueRef.current.push({
          chipUid: result.uid,
          locked: Boolean(result.locked),
          family,
          lock,
          productSku,
          productTitle,
          productImage,
          sdmMetaReadKey: result.sdmMetaReadKey,
          sdmFileReadKey: result.sdmFileReadKey,
          target,
        })
        pumpProvision()
      }
      // Loop exited (stop/cancel/next-box/done cleared runningRef, or the fatal-disconnect break
      // above) — release the heartbeat, but ONLY if a newer run hasn't taken over (else it owns
      // the interval and we'd kill its lock). A superseding run already cleared+restarted it.
      if (runGenRef.current === myGen && keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current)
        keepAliveIntervalRef.current = null
      }
    },
    [encode, keepAlive, upsertEncoded, pumpProvision],
  )

  // Stop the loop (disarm the reader) without clearing the running list — used by "Next box", the
  // container switching target, and unmount cleanup. Bumps the generation so any in-flight loop
  // bails even before its own `runningRef` check (it may be blocked inside `encode()`).
  const stopSession = useCallback(() => {
    runGenRef.current++
    runningRef.current = false
    setSessionActive(false)
    setWaiting(false)
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current)
      keepAliveIntervalRef.current = null
    }
  }, [])

  // Clear all per-target session state (running list, counts, alert, filter) — used between bulk
  // boxes after stopSession. Does NOT touch runningRef (stopSession owns the loop lifecycle).
  const resetSession = useCallback(() => {
    setEncoded([])
    setAlert(null)
    setShowQuarantinedOnly(false)
    setPackageTagCount(null)
    provisionQueueRef.current = []
  }, [])

  // VERA-790 Phase 2 — safety net: clear the heartbeat interval on unmount (closing the modal /
  // navigating away mid-session) even though the loop's own post-loop cleanup normally handles it
  // — an unmount can race ahead of the loop noticing `runningRef` flipped, so this guarantees no
  // orphaned interval keeps pinging a closed WebSocket after the container is gone.
  useEffect(() => {
    return () => {
      runningRef.current = false
      if (keepAliveIntervalRef.current) clearInterval(keepAliveIntervalRef.current)
    }
  }, [])

  // VERA-790 — "Cancel": un-assign THIS session's freshly-provisioned tags from the package. Only
  // `status === 'provisioned'` rows count as "added this session" — `already` rows were in the
  // package before this session started (leave them), and quarantined/provisioning rows never
  // landed a package association to undo. Does the POST + the standard confirmation/error toast;
  // returns whether it succeeded so the container decides what to do next (navigate vs close).
  const sessionAddedUids = encoded.filter((t) => t.status === 'provisioned').map((t) => t.uid)
  const sessionAddedCount = sessionAddedUids.length
  const cancelSession = useCallback(
    async (packageId: string): Promise<boolean> => {
      const uids = encoded.filter((t) => t.status === 'provisioned').map((t) => t.uid)
      if (!uids.length) return false
      setCancelling(true)
      try {
        await api.post(`/packages/${packageId}/tags/unassign`, { uids })
        // Plain confirmation, no Undo: Cancel is deliberate and the tags aren't lost — they stay
        // encoded and land in the Tags list unassigned, re-assignable from there (or via a
        // package's "Add here instead" conflict flow).
        notification({
          type: 'success',
          title: `${uids.length} tag${uids.length === 1 ? '' : 's'} removed from this package`,
          text: "They're encoded and unassigned in Tags.",
        })
        return true
      } catch (error) {
        notification({
          type: 'error',
          title: getErrorMessage(error) || 'Could not remove tags from this package',
        })
        return false
      } finally {
        setCancelling(false)
      }
    },
    [encoded, notification],
  )

  // VERA-790 Part C — "Add here instead": the operator's explicit confirmation to MOVE a tag off
  // the other package it's already in and into THIS one. Replays the exact job the tap originally
  // queued (same family/lock/product identity/target — this session's package), with `reassign:
  // true` so the backend proceeds instead of conflicting again. Goes back through the same bounded
  // queue as a normal tap, not a one-off request, so it can't blow past PROVISION_CONCURRENCY.
  const retryAsReassign = useCallback(
    (row: EncodedTag) => {
      if (!row.conflictJob) return
      upsertEncoded({ uid: row.uid, locked: row.locked, status: 'provisioning' })
      provisionQueueRef.current.push({ ...row.conflictJob, reassign: true })
      pumpProvision()
    },
    [upsertEncoded, pumpProvision],
  )

  return {
    // reader stream passthrough (bulk scan-resolve subscribes to this)
    subscribeScan,
    // list + counts
    encoded,
    visibleRows,
    provisioned,
    quarantined,
    provisioning,
    alreadyInPackageCount,
    packageTagCount,
    // lifecycle state
    sessionActive,
    waiting,
    alert,
    setAlert,
    showQuarantinedOnly,
    setShowQuarantinedOnly,
    cancelling,
    sessionAddedCount,
    sessionAddedUids,
    // controls
    runSession,
    stopSession,
    resetSession,
    seedPackageCount,
    cancelSession,
    retryAsReassign,
  }
}
